"use client"

import { useState, useEffect, useRef } from "react"
import { Button } from "./button.jsx"
import { Input } from "./input.jsx"
import { Label } from "./label.jsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select.jsx"
import { Card, CardContent, CardHeader, CardTitle } from "./card.jsx"
import { Badge } from "./badge.jsx"
import { Database, Table, Columns, Eye, RefreshCw } from "lucide-react"

export function DynamicDataSelector({
    MOCK_TABLES = [
        {
            name: "sales_data",
            description: "銷售數據表",
            fields: ["id", "product_name", "sales_amount", "sales_date", "region", "quantity"],
            sampleData: [
                { product_name: "產品A", sales_amount: 15000, sales_date: "2024-01", region: "北部", quantity: 50 },
                { product_name: "產品B", sales_amount: 22000, sales_date: "2024-01", region: "南部", quantity: 73 },
            ],
        },
        {
            name: "user_analytics",
            description: "用戶分析表",
            fields: ["date", "active_users", "new_users", "page_views", "session_duration"],
            sampleData: [
                { date: "2024-01-01", active_users: 1250, new_users: 89, page_views: 5420, session_duration: 245 },
                { date: "2024-01-02", active_users: 1340, new_users: 95, page_views: 5890, session_duration: 267 },
            ],
        },
        {
            name: "inventory",
            description: "庫存管理表",
            fields: ["product_id", "product_name", "stock_level", "reorder_point", "supplier", "last_updated"],
            sampleData: [
                {
                    product_name: "筆記本電腦",
                    stock_level: 45,
                    reorder_point: 20,
                    supplier: "供應商A",
                    last_updated: "2024-01-15",
                },
                { product_name: "滑鼠", stock_level: 120, reorder_point: 50, supplier: "供應商B", last_updated: "2024-01-14" },
            ],
        },
        {
            name: "financial_reports",
            description: "財務報表",
            fields: ["month", "revenue", "expenses", "profit", "growth_rate", "department"],
            sampleData: [
                { month: "2024-01", revenue: 500000, expenses: 320000, profit: 180000, growth_rate: 12.5, department: "銷售部" },
                { month: "2024-02", revenue: 580000, expenses: 350000, profit: 230000, growth_rate: 15.2, department: "銷售部" },
            ],
        },
    ],
    selectedTable,
    onTableChange,
    xAxisField,
    yAxisField,
    onAxisChange,
}) {
    const [showPreview, setShowPreview] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [open, setOpen] = useState(false)
    const searchInputRef = useRef(null)

    useEffect(() => {
        if (open) {
            // focus the search input when dropdown opens
            // small timeout to ensure element is mounted in portal
            setTimeout(() => {
                try {
                    searchInputRef.current?.focus()
                } catch (e) { }
            }, 0)
        }
    }, [open])
    // Optional: clear search term when dropdown closes
    useEffect(() => {
        if (!open) setSearchTerm("")
    }, [open])

    // Ensure each table has a stable id (use provided id or fallback to index)
    const tablesWithId = (MOCK_TABLES || []).map((t, idx) => ({ _id: t?.id ?? idx, ...t }))
    const selectedTableData = tablesWithId.find((table) => `${table._id}` === `${selectedTable}`)
    const availableFields = selectedTableData?.fields || []

    // Filter tables by search term (search name, description, and fields)
    const filteredTables = tablesWithId.filter((table) => {
        if (!searchTerm) return true
        const q = searchTerm.toLowerCase()
        return (
            (table.name || "").toLowerCase().includes(q) ||
            (table.description || "").toLowerCase().includes(q) ||
            (table.fields || []).some((f) => (`${f}` || "").toLowerCase().includes(q))
        )
    })

    return (
        <div className=" ">
            {/* Table Selection */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Table className="h-4 w-4" />
                        選擇資料表
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Select value={selectedTable} onValueChange={onTableChange} open={open} onOpenChange={setOpen}>
                        <SelectTrigger>
                            <SelectValue placeholder="請選擇要使用的資料表" />
                        </SelectTrigger>
                        <SelectContent>
                            {/* 搜尋輸入：放在下拉內容最上方 */}
                            <div className="p-2">
                                <Input
                                    ref={searchInputRef}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="搜尋表名、描述或欄位"
                                    className="w-full bg-muted/5"
                                    // Prevent Radix Select typeahead / keyboard handlers from
                                    // intercepting key events while typing in the search box.
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onKeyUp={(e) => e.stopPropagation()}
                                    onKeyPress={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            {filteredTables.length === 0 ? (
                                <div className="p-3 text-sm text-muted-foreground">沒有符合的資料表</div>
                            ) : (
                                filteredTables.map((table) => (
                                    <SelectItem key={table._id} value={`${table._id}`}>
                                        <div className="flex flex-col items-start py-1">
                                            <span className="font-medium">{table?.name}</span>
                                            <span className="text-xs text-muted-foreground">{table?.description}</span>
                                            <span className="text-xs text-muted-foreground">{(table?.fields || []).length} 個欄位</span>
                                        </div>
                                    </SelectItem>
                                ))
                            )}
                        </SelectContent>
                    </Select>

                    {selectedTableData && (
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs flex items-center gap-1 mb-2">
                                    <Columns className="h-3 w-3" />
                                    資料表欄位 ({(selectedTableData?.fields || []).length})
                                </Label>
                                <div className="grid grid-cols-2 gap-1">
                                    {(selectedTableData?.fields || []).map((field) => (
                                        <Badge key={field?.code} variant="secondary" className="text-xs justify-start">
                                            {field?.code}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

        </div>
    )
}

